import { useEffect, useState } from "react";
import { BODY_SPOTS, FIGURE_SETS, REGION_NAMES, figureSrc } from "@/lib/bodyRegions";
import { api } from "@/lib/auth";

const LEVELS = ["", "A flicker", "A current", "Radiant"];

const Glow = ({ v }) => (v ? <span aria-hidden className="absolute rounded-full -translate-x-1/2 -translate-y-1/2 left-1/2 top-1/2 pointer-events-none" style={{ width: 18 + v * 12, height: 18 + v * 12, background: `radial-gradient(circle, rgba(242,200,140,${0.4 + v * 0.18}) 0%, rgba(194,65,79,${0.3 + v * 0.15}) 45%, transparent 72%)` }} /> : null);

const Figure = ({ figure, name, levels = {}, onTap, testId }) => (
  <div data-testid={testId} className="min-w-0">
    <p className="text-center font-tt-mono text-[11px] uppercase tracking-[0.2em] text-[#D9B47A] truncate">{name}</p>
    <div className="relative mt-2 w-full aspect-[848/1264] overflow-hidden">
      <img src={figureSrc(figure)} alt={`${figure} figure`} draggable={false} className="absolute inset-0 w-full h-full object-cover select-none" />
      {BODY_SPOTS.map(([k, x, y], i) => (
        <button key={`${k}-${i}`} type="button" disabled={!onTap} onClick={() => onTap?.({ ...levels, [k]: ((levels[k] ?? 0) + 1) % 4 })} aria-label={`${REGION_NAMES[k]} ${LEVELS[levels[k] ?? 0]}`}
          data-testid={`${testId}-spot-${k}${i === 8 ? "-r" : ""}`} className="absolute w-8 h-8 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center disabled:cursor-default" style={{ left: `${x}%`, top: `${y}%` }}>
          <Glow v={levels[k] ?? 0} />
          <span className={`relative w-2 h-2 rounded-full border ${levels[k] ? "bg-[#F2E8DF] border-[#F2E8DF]" : onTap ? "border-[#D9B47A]/70 bg-[#0A0706]/60" : "border-transparent"}`} />
        </button>
      ))}
    </div>
  </div>
);

// Two figures side by side; each partner taps where they felt the current (tap again to brighten).
export default function CoupleMap({ names, maps, figures = "fm", editable = [false, false], onChange, testId = "couple-map" }) {
  const figs = FIGURE_SETS[figures]?.length === 2 ? FIGURE_SETS[figures] : FIGURE_SETS.fm;
  return (
    <div data-testid={testId} className="grid grid-cols-2 gap-3">
      {[0, 1].map((i) => <Figure key={i} figure={figs[i]} name={names[i]} levels={maps[i] ?? {}} onTap={editable[i] ? (v) => onChange(i, v) : null} testId={`${testId}-${i ? "b" : "a"}`} />)}
    </div>
  );
}

const when = (iso) => new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });

// Journal: shared maps saved after partner breath.
export function BreathMapsJournal() {
  const [list, setList] = useState(null);
  useEffect(() => {
    api.get("/breath/maps").then(({ data }) => setList(Array.isArray(data) ? data : [])).catch(() => setList([]));
  }, []);
  if (!list?.length) return null;
  return (
    <section data-testid="journal-breath-maps" className="max-w-xl mx-auto px-5 mt-12">
      <p className="font-tt-mono text-[11px] uppercase tracking-[0.25em] text-[#D9B47A]">Partner breath · where you felt it</p>
      <ul className="mt-4 space-y-6">
        {list.map((m, k) => (
          <li key={`${m.at}-${k}`} data-testid={`journal-breath-map-${k}`} className="border border-[#D9B47A]/20 p-4">
            <p className="text-[13px] text-[#A8998C]">{(m.names ?? []).join(" & ")} · {when(m.at)}{m.rhythm ? ` · ${m.rhythm === "mirrored" ? "Mirrored" : "In unison"}` : ""}</p>
            <div className="mt-3 max-w-[340px] mx-auto"><CoupleMap names={m.names ?? ["", ""]} maps={m.maps ?? []} figures={m.figures} testId={`journal-couple-map-${k}`} /></div>
          </li>
        ))}
      </ul>
    </section>
  );
}
